import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import UnarchiveIcon from "@mui/icons-material/Unarchive";
import { isEqual } from "lodash";
import { useNavigate } from "react-router-dom";
import useSWR from "swr";

import { getConditionByWorkspaceID, setConditionArchieve } from "../../Api/Services/ConditionsServices.js";
import { getPetNamesByWorkspaceId } from "../../Api/Services/PetServices.js";
import "./GlobalItemList.css";
import { ReactComponent as ViewIcon } from "../../Assets/Images/viewIcon.svg";
import useAuth from "../../ContextProvider/AuthProvider/AuthProvider.js";
import { filterAscDescData, hexToRgba } from "../../Utils.js";
import DetailsModal from "../Common/DetailsModal.js";
import PawItemList from "../Common/PawItemList.js";
import { Button } from "@mui/material";
import { useTheme } from "@mui/material/styles";

export default function Conditions() {
  const { workspaceId: w_id } = useAuth();
  const [selectedPetName, setSelectedPetName] = useState("");
  const [open, setOpen] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [archiveId, setArchiveId] = useState(null);
  const [filter, setFilter] = useState({});
  const [pageCount, setPageCount] = useState(0);
  const [selConditionData, setSelConditionData] = useState([]);
  const fetchIdRef = useRef(0);
  const theme = useTheme();

  const navigate = useNavigate();
  const handleArchiveClick = (medication_id) => {
    setShowSuccessMessage(true);
    setOpen(true);
    setArchiveId(medication_id); 
  };
  const handleArchive = () => {
    setIsUpdating(true);

    setConditionArchieve(archiveId)
      .then((response) => {
        setIsUpdating(false);
        console.log(response);
        if (response.data.success === true) {
          window.location.reload();
        }
      })
      .catch((err) => {
        console.error(err);
        setOpen(true);
        setIsUpdating(false);
      });
  };

  const { data: conditionsData, isLoading } = useSWR(
    w_id ? `conditionsData${w_id}` : null,
    async () => {
      const { data } = await getConditionByWorkspaceID(w_id);
      const filterData = data?.data?.map(item => {
        return {
          ...item,
          id: item.condition_id,
        }
      })
      return filterData || [];
    },
    {
      revalidateOnFocus: false,
    }
  );

  const fetchData = useCallback((oFilter) => {
    const fetchId = ++fetchIdRef.current;
    if (fetchId === fetchIdRef.current && !isEqual(filter, oFilter)) {
      setFilter(oFilter);
    }
  }, []);

  useEffect(() => {
    const { limit, from, sort } = filter;
    if (filter && filter.from > -1) {
      if (conditionsData?.length && limit) {
        setPageCount(Math.ceil((conditionsData?.length) / limit));
        const allData = [...conditionsData];
        const filteredResult = filterAscDescData(allData, sort, from, limit);
        setSelConditionData(filteredResult);
      }
    }
  }, [filter, conditionsData]);

  const { data: petNames = [] } = useSWR(
    w_id ? `petNames${w_id}` : null,
    async () => {
      const { data } = await getPetNamesByWorkspaceId(w_id);
      return data?.data || [];
    },
    {
      revalidateOnFocus: false,
    }
  );

  let filteredConditionData;

  if (selectedPetName === "") {
    filteredConditionData = selConditionData;
  } else if (!selConditionData) {
    filteredConditionData = [];
  } else {
    filteredConditionData = selConditionData?.filter((item) => {
      const petName = item.name.toLowerCase() || "";
      const query = selectedPetName.toLowerCase();
      return petName.includes(query);
    });
  }

  const petNamesSet = new Set();
  const filteredPetNames = petNames?.filter((pet) => {
    const lowercaseName = pet.name.toLowerCase();
    if (!petNamesSet.has(lowercaseName)) {
      petNamesSet.add(lowercaseName);
      return true;
    }
    return false;
  });

  const columns = useMemo(() => {
    return [
      {
        Header: "",
        accessor: "image",
        width: "8vw",
        disableSortBy: true,
        Cell: ({ row }) => {
          return row?.original?.image ? (
            <img
              alt="petImage"
              src={row?.original?.image}
              style={{
                width: "50px",
                height: "50px",
                borderRadius: "10px",
                objectFit: "cover",
                margin: "0 auto",
                verticalAlign: "middle",
              }}
            />
          ) : (
            <AccountCircleIcon
              sx={{
                width: "50px",
                height: "50px",
              }}
            />
          );
        },
      },
      {
        Header: "Pet Name",
        accessor: "name",
        width: "12.5vw",
      },
      {
        Header: `Condition`,
        accessor: "conditions",
        width: "12.5vw",
      },
      {
        Header: "Notes",
        accessor: "condition_note",
        width: "12.5vw",
      },
      {
        Header: "",
        accessor: "condition_id",
        width: "8vw",
        isArchive: true,
        disableSortBy: true,
        disabled: isUpdating,
        textAlign: "center",
        Cell: ({ row }) => {
          return (
            <UnarchiveIcon
              style={{
                color: "#000000",
              }}
              onClick={() =>
                handleArchiveClick(row?.original?.condition_id)
              }
            />
          );
        },
      },
      {
        Header: "Action",
        accessor: "action",
        width: "8vw",
        disableSortBy: true,
        textAlign: "center",
        Cell: ({ row }) => {
          return (
            <Button
              sx={{
                minWidth: "40px",
                background: `${hexToRgba(theme.palette.primary.main, 0.20)} !important`,
                borderRadius: "10px",
                marginRight: "5px",
                width: "24px",
              }}
              onClick={() => navigate(props.viewDataLink(row.original))}
            >
               <ViewIcon
                style={{
                  fill: `${theme.palette.primary.main}`, 
                  cursor: "pointer",
                }}
                alt="view"
              />
            </Button>
          );
        },
      },
    ];
  }, []);

  const defaultOptions = {
    pageSize: 5,
  };

  const props = {
    label: "Conditions",
    setSelectedUserName: setSelectedPetName,
    items: filteredPetNames,
    isLoading: isLoading,
    dataAddLink: "/conditions/form",
    columns,
    columnsData: filteredConditionData,
    viewDataLink: (data) => `/conditions/info/${data.condition_id}`,
    placeholder: "pet name",
    details: "There are no condition details for this user",
    fetchData,
    pageCount,
    defaultOptions,
  };

  const ButtonItem = [
    {
      name: "yes",
      onClick: () => {
        handleArchive();
        setShowSuccessMessage(false);
      },
    },
    {
      name: "no",
      onClick: () => {
        setOpen(false);
        setShowSuccessMessage(false);
      },
    },
  ];

  return (
    <>
      <PawItemList {...props} />
      {showSuccessMessage && (
        <>
          <DetailsModal
            open={open}
            severity="warning"
            buttonList={ButtonItem}
            title="Do you want to archive this condition?"
            handleClose={()=> setOpen(false)}
          />
        </>
      )}
    </>
  );
}
